<template>
  <EwnFormControl vid="new-password" label-for="new-password" class="password-control" :label="$t('login.new-password-label')"
                  v-slot="{ state }" :rules="{ required: true, min: 8, max: 128, password: [requirements] }">
    <b-form-input id="new-password" type="password" name="new-password" :state="state"
                  :value="value" @input="$emit('input', $event)" autocapitalize="off"
                  autocomplete="new-password" autocorrect="off" spellcheck="false" />
    <b-popover target="new-password" triggers="focus" placement="right"
               fallback-placement="counterclockwise" :disabled="!isMediumScreen"
               :title="$t('password.requirements-label')">
      <ul class="requirements-list">
        <li>
          {{ $t('password.requirements.length') }}
          <PasswordRequirementIcon :is-met="requirements.hasLength" />
        </li>
        <li>
          {{ $t('password.requirements.complexity') }}
          <PasswordRequirementIcon :is-met="requirements.hasEnough" />
          <ul class="complexity-list">
            <li>
              {{ $t('password.requirements.uppercase') }}
              <PasswordRequirementIcon :is-met="requirements.hasUpper" />
            </li>
            <li>
              {{ $t('password.requirements.lowercase') }}
              <PasswordRequirementIcon :is-met="requirements.hasLower" />
            </li>
            <li>
              {{ $t('password.requirements.number') }}
              <PasswordRequirementIcon :is-met="requirements.hasNumber" />
            </li>
            <li>
              {{ $t('password.requirements.special' )}}
              <PasswordRequirementIcon :is-met="requirements.hasSpecial" />
              <ul class="special-list">
                  <li>{{ $t('password.requirements.special-example') }}</li>
              </ul>
            </li>
          </ul>
        </li>
        <li>
          {{ $t('password.requirements.repeat') }}
          <PasswordRequirementIcon :is-met="!requirements.hasRepeats" />
        </li>
        <li>{{ $t('password.requirements.username') }}</li>
      </ul>
    </b-popover>
    <template v-if="strength">
      <span class="password-strength" :class="`score-${strength.score}`">
        {{ strengthName }}
      </span>
      <div class="warning-feedback" v-if="state">{{ strength.feedback.warning }}</div>
    </template>
  </EwnFormControl>
</template>

<script>
  import { useGridSystem } from '@ewn/vueuse';
  import { computed, defineComponent, toRefs } from 'vue';
  import { zxcvbn } from '@zxcvbn-ts/core';
  import { BFormInput, BPopover, VBPopover } from 'bootstrap-vue';
  import { useI18n } from '@/i18n';
  import { validatePassword } from '../validatePassword';
  import EwnFormControl from './EwnFormControl';
  import PasswordRequirementIcon from './PasswordRequirementIcon';

  export default defineComponent({
    name: 'NewPasswordControl',
    components: {
      'b-form-input': BFormInput,
      'b-popover': BPopover,
      EwnFormControl,
      PasswordRequirementIcon
    },
    directives: {
      'b-popover': VBPopover
    },
    emits: ['input'],
    props: {
      value: {
        type: String
      }
    },
    setup (props) {
      const i18n = useI18n();
      const { value } = toRefs(props);
      const strength = computed(() => value.value ? zxcvbn(value.value) : null);
      const strengthName = computed(() => strength.value
        ? i18n.t(`password.score.${strength.value.score}`)
        : null);
      const requirements = computed(() => validatePassword(value.value));
      const { md: isMediumScreen } = useGridSystem();
      return {
        isMediumScreen,
        requirements,
        strength,
        strengthName
      };
    }
  });
</script>

<style lang="scss" scoped>
  @use '@/styles/modules/palette';

  .password-control::v-deep .form-group {
    position: relative;
  }

  .password-strength {
    position: absolute;
    top: 0;
    right: 0;
    font-size: 14px;
    font-weight: 600;

    &.score-0,
    &.score-1 {
      color: palette.$status-error-perceptual-color;
    }

    &.score-2 {
      color: palette.$status-warning-color;
    }

    &.score-3,
    &.score-4 {
      color: palette.$status-success-color;
    }
  }

  .requirements-list {
    list-style: disc;
    margin-left: 18px;
  }

  .complexity-list {
    list-style: circle;
    margin-left: 20px;
  }

  .special-list {
    list-style: square;
    margin-left: 18px;
  }
</style>
